import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, ArrowUp, ArrowDown, Upload, Plus } from "lucide-react";
import * as Icons from "lucide-react";
import api, { apiError } from "../../lib/api";
import { uploadImage } from "../../lib/upload";
import { CATEGORY_ICONS } from "../CategoryTiles";
import { MobileHomeControls } from "./MobileHomeControls";

const COLORS = ["#A3E635", "#93C5FD", "#FDBA74", "#FCD34D", "#F9A8D4", "#C4B5FD", "#F2FBE3", "#FFFFFF"];

function ImgUpload({ label, value, onChange, spec, testId }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); toast.success("Uploaded"); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="nb-label">{label} <span className="normal-case tracking-normal text-slate-400">({spec})</span></label>
      <div className="mt-1.5 flex items-center gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-file`} />
        <button data-testid={testId} disabled={busy} onClick={() => ref.current.click()} className="nb-btn nb-btn-sm"><Upload className="w-3.5 h-3.5" /> {busy ? "…" : "Upload"}</button>
        {value && <img src={value} alt="" className="h-9 w-14 object-cover rounded-lg border-2 border-[#171B33]" />}
        {value && <button onClick={() => onChange("")} className="text-xs text-slate-400 hover:text-red-600">remove</button>}
      </div>
    </div>
  );
}

export default function HomepageTab({ onBanners }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/admin/homepage").then((r) => setH(r.data)).catch(() => {}); }, []);
  if (!h) return <p className="mt-8 text-sm text-slate-500">Loading…</p>;

  const save = async () => {
    try { const r = await api.put("/admin/homepage", h); toast.success(r.data.message); } catch (err) { toast.error(apiError(err)); }
  };
  const setCat = (i, patch) => setH({ ...h, categories: h.categories.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const moveCat = (i, d) => { const a = [...h.categories]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setH({ ...h, categories: a }); };
  const addCat = () => setH({ ...h, categories: [...h.categories, { id: `cat_${Date.now()}`, label: "New Category", icon: "Star", color: "#F2FBE3", link: "/institutes", image_mobile: "", image_desktop: "", active: true }] });
  const setSec = (i, patch) => setH({ ...h, sections: h.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  return (
    <div className="mt-8 space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-extrabold">Homepage Builder</h2>
          <p className="text-sm text-slate-600">Control every block of the home page. Image sizes: hero banner <b>desktop 1600×600</b>, <b>mobile 800×400</b>; strip banner <b>1600×300</b> / mobile <b>800×200</b>; category tile <b>400×400</b> square; splash <b>1080×1920</b>.</p>
        </div>
        <button data-testid="homepage-save-btn" onClick={save} className="nb-btn nb-btn-lime"><Save className="w-4 h-4" /> Save Homepage</button>
      </div>

      <MobileHomeControls value={h.mobile} onChange={(mobile) => setH({ ...h, mobile })} onBanners={onBanners} />

      <section className="nb p-6">
        <h3 className="font-display font-extrabold mb-4">Hero text (desktop)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="nb-label">Title — use {"{city}"} for the city name</label>
            <input data-testid="home-hero-title-input" value={h.hero_title || ""} onChange={(e) => setH({ ...h, hero_title: e.target.value })} className="nb-input mt-1.5" /></div>
          <div><label className="nb-label">Subtitle</label>
            <input data-testid="home-hero-subtitle-input" value={h.hero_subtitle || ""} onChange={(e) => setH({ ...h, hero_subtitle: e.target.value })} className="nb-input mt-1.5" /></div>
        </div>
      </section>

      <section className="nb p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold">Browse by Category tiles</h3>
          <button data-testid="home-add-category-btn" onClick={addCat} className="nb-btn nb-btn-sm"><Plus className="w-4 h-4" /> Add tile</button>
        </div>
        <div className="space-y-4">
          {h.categories.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Star;
            return (
              <div key={c.id} data-testid={`home-category-row-${c.id}`} className="nb-flat p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-start" style={{ borderRadius: 12, opacity: c.active ? 1 : 0.55 }}>
                <div className="lg:col-span-1 flex lg:flex-col items-center gap-2">
                  <span className="w-12 h-12 rounded-xl border-2 border-[#171B33] flex items-center justify-center" style={{ background: c.color }}><Icon className="w-6 h-6" /></span>
                  <div className="flex lg:flex-col gap-1 text-slate-400">
                    <button onClick={() => moveCat(i, -1)} data-testid={`home-cat-up-${c.id}`}><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => moveCat(i, 1)} data-testid={`home-cat-down-${c.id}`}><ArrowDown className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="lg:col-span-3"><label className="nb-label">Label</label>
                  <input data-testid={`home-cat-label-${c.id}`} value={c.label} onChange={(e) => setCat(i, { label: e.target.value })} className="nb-input mt-1.5" /></div>
                <div className="lg:col-span-3"><label className="nb-label">Link</label>
                  <input data-testid={`home-cat-link-${c.id}`} value={c.link} onChange={(e) => setCat(i, { link: e.target.value })} className="nb-input mt-1.5" /></div>
                <div className="lg:col-span-2"><label className="nb-label">Icon</label>
                  <select data-testid={`home-cat-icon-${c.id}`} value={c.icon} onChange={(e) => setCat(i, { icon: e.target.value })} className="nb-input mt-1.5">
                    {CATEGORY_ICONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select></div>
                <div className="lg:col-span-2"><label className="nb-label">Color</label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {COLORS.map((col) => <button key={col} onClick={() => setCat(i, { color: col })} className="w-6 h-6 rounded-md border-2" style={{ background: col, borderColor: c.color === col ? "#171B33" : "#CBD5E1" }} />)}
                  </div></div>
                <div className="lg:col-span-1 flex flex-col gap-2 items-end">
                  <label className="flex items-center gap-1.5 text-xs font-bold"><input type="checkbox" data-testid={`home-cat-active-${c.id}`} checked={c.active} onChange={(e) => setCat(i, { active: e.target.checked })} /> Active</label>
                  <button data-testid={`home-cat-delete-${c.id}`} onClick={() => setH({ ...h, categories: h.categories.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="lg:col-span-6"><ImgUpload label="Tile image — mobile" spec="400×400 px, replaces icon" value={c.image_mobile} onChange={(v) => setCat(i, { image_mobile: v })} testId={`home-cat-img-mobile-${c.id}`} /></div>
                <div className="lg:col-span-6"><ImgUpload label="Tile image — desktop" spec="400×400 px" value={c.image_desktop} onChange={(v) => setCat(i, { image_desktop: v })} testId={`home-cat-img-desktop-${c.id}`} /></div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="nb p-6">
        <h3 className="font-display font-extrabold mb-4">Featured sections</h3>
        <div className="space-y-3">
          {h.sections.map((s, i) => (
            <div key={s.id} className="nb-flat p-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" style={{ borderRadius: 12 }}>
              <div className="sm:col-span-6"><label className="nb-label">Title ({s.type})</label>
                <input data-testid={`home-section-title-${s.id}`} value={s.title} onChange={(e) => setSec(i, { title: e.target.value })} className="nb-input mt-1.5" /></div>
              <div className="sm:col-span-3"><label className="nb-label">Max cards</label>
                <input data-testid={`home-section-limit-${s.id}`} type="number" min={1} max={12} value={s.limit} onChange={(e) => setSec(i, { limit: Number(e.target.value) })} className="nb-input mt-1.5" /></div>
              <label className="sm:col-span-3 flex items-center gap-2 text-sm font-bold pb-3"><input type="checkbox" data-testid={`home-section-enabled-${s.id}`} checked={s.enabled} onChange={(e) => setSec(i, { enabled: e.target.checked })} /> Show on home</label>
            </div>
          ))}
        </div>
      </section>

      <section className="nb p-6">
        <h3 className="font-display font-extrabold mb-4">Other blocks</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" data-testid="home-show-ranks" checked={h.show_how_it_ranks !== false} onChange={(e) => setH({ ...h, show_how_it_ranks: e.target.checked })} /> Show "How we rank" block</label>
          <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" data-testid="home-show-cta" checked={h.show_cta !== false} onChange={(e) => setH({ ...h, show_cta: e.target.checked })} /> Show "List your institute" call-to-action</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="nb-label">CTA title</label>
              <input data-testid="home-cta-title" value={h.cta_title || ""} onChange={(e) => setH({ ...h, cta_title: e.target.value })} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">CTA text</label>
              <input data-testid="home-cta-text" value={h.cta_text || ""} onChange={(e) => setH({ ...h, cta_text: e.target.value })} className="nb-input mt-1.5" /></div>
          </div>
        </div>
      </section>
      <button data-testid="homepage-save-btn-bottom" onClick={save} className="nb-btn nb-btn-lime"><Save className="w-4 h-4" /> Save Homepage</button>
    </div>
  );
}
